import React, { FC, useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import PersonIdIcon from '@mui/icons-material/AssignmentIndOutlined';
import ApprovalIcon from '@mui/icons-material/ApprovalOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import GroupWorkIcon from '@mui/icons-material/GroupWorkOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PhoneIcon from '@mui/icons-material/MobileFriendlyOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import DomainVerifiedIcon from '@mui/icons-material/DomainVerificationOutlined';
import SettingIcon from '@mui/icons-material/SettingsOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptLongOutlined';
import SellIcon from '@mui/icons-material/SellOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearchOutlined';
import useAuth from 'hooks/useAuth';
import NavSection, { getItems, NavItemModel } from '../NavSection';
import Scrollbar from '../Scrollbar';
import { hasValue } from '../inputs/inputHelpers';
import ContactUsButton from './ContactUsButton';
import { appPermissions, localRoutes, productLabel, remoteRoutes, rootAdminUserRoles } from '../../constants';
import { appSettings, remoteConfigData } from '../../config';
import VerifyLogo from '../../logos/VerifyLogo';
import { toTitleCase } from '../../utils/stringHelpers';
import { ProductCategory } from '../../__generated__/kycTypes';
import useAppTheme from '../../hooks/useAppTheme';
import NewLabel from '../NewLabel';


interface DashboardSidebarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

type Section = {
  title: string,
  items: NavItemModel[]
};

const getSections = (
  enableCustomerOnboarding: boolean,
  enablePeopleBulkImports: boolean,
  enableCompanyBulkImports: boolean,
  enableUssdConsent: boolean
): Section[] => {
  return [
    {
      title: 'Customers',
      items: [
        {
          title: 'People',
          icon: <PeopleIcon fontSize="small"/>,
          path: '/',
          roles: [appPermissions.CUSTOMERS_VIEW],
          children: [
            {
              title: 'Profiles',
              path: localRoutes.people,
              roles: [appPermissions.CUSTOMERS_VIEW]
            },
            enablePeopleBulkImports &&
                        {
                          title: 'Bulk Imports',
                          path: localRoutes.bulkPeopleProfileImports,
                          roles: [appPermissions.CUSTOMERS_VIEW]
                        }
          ]
        },
        {
          title: 'Companies',
          icon: <BusinessIcon fontSize="small"/>,
          path: localRoutes.companies,
          roles: [appPermissions.CUSTOMERS_VIEW],
          children: [
            {
              title: 'Profiles',
              path: localRoutes.companies,
              roles: [appPermissions.CUSTOMERS_VIEW]
            },
            enableCompanyBulkImports &&
                        {
                          title: 'Bulk Imports',
                          path: localRoutes.bulkCompanyProfileImports,
                          roles: [appPermissions.CUSTOMERS_VIEW]
                        },
          ]
        },
      ]
    },
    {
      title: 'Products',
      items: [
        enableCustomerOnboarding &&
                {
                  title: productLabel(ProductCategory.CustomerOnboarding),
                  icon: <PersonAddAltIcon fontSize="small"/>,
                  path: localRoutes.singleOnboardingRequests,
                  info: <NewLabel />,
                  roles: [appPermissions.EDIT_ONBOARDING_TEMPLATES, appPermissions.VIEW_ONBOARDING_TEMPLATES, appPermissions.BULK_ONBOARDING_VIEW_REQUESTS, appPermissions.BULK_ONBOARDING_TRIGGER, appPermissions.ONBOARDING_VIEW_REQUESTS, appPermissions.ONBOARDING_TRIGGER],
                  children: [
                    {
                      title: 'Cases',
                      path: localRoutes.onboardingCases,
                      roles: [appPermissions.ONBOARDING_VIEW_REQUESTS, appPermissions.ONBOARDING_TRIGGER],
                    },
                    {
                      title: 'Bulk Requests',
                      path: localRoutes.bulkOnboardingRequests,
                      roles: [appPermissions.BULK_ONBOARDING_VIEW_REQUESTS, appPermissions.BULK_ONBOARDING_TRIGGER],
                    },
                    {
                      title: 'Templates',
                      path: localRoutes.onboardingTemplates,
                      roles: [appPermissions.VIEW_ONBOARDING_TEMPLATES, appPermissions.EDIT_ONBOARDING_TEMPLATES]
                    }
                  ]
                },
        {
          title: productLabel(ProductCategory.IdVerification),
          icon: <PersonIdIcon fontSize="small"/>,
          path: localRoutes.nationalIdVerifications,
          roles: [appPermissions.ID_VERIFICATION_VIEW, appPermissions.ID_VERIFICATION_TRIGGER, appPermissions.BULK_ID_VERIFICATION_VIEW, appPermissions.BULK_ID_VERIFICATION_TRIGGER],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.nationalIdVerifications,
              roles: [appPermissions.ID_VERIFICATION_VIEW, appPermissions.ID_VERIFICATION_TRIGGER,],
            },
            {
              title: 'Bulk Requests',
              path: localRoutes.bulkNationalIdVerifications,
              roles: [appPermissions.BULK_ID_VERIFICATION_VIEW, appPermissions.BULK_ID_VERIFICATION_TRIGGER]
            },
          ]
        },
        {
          title: productLabel(ProductCategory.FaceVerification),
          icon: <PersonSearchIcon fontSize="small"/>,
          path: localRoutes.faceVerifications,
          info: <NewLabel />,
          roles: [appPermissions.FACE_VERIFICATION_TRIGGER, appPermissions.FACE_VERIFICATION_VIEW, appPermissions.CUSTOMER_FACE_VERIFICATION_TRIGGER, appPermissions.BULK_FACE_VERIFICATION_TRIGGER, appPermissions.BULK_FACE_VERIFICATION_VIEW],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.faceVerifications,
              roles: [appPermissions.FACE_VERIFICATION_TRIGGER, appPermissions.CUSTOMER_FACE_VERIFICATION_TRIGGER, appPermissions.FACE_VERIFICATION_VIEW],
            },
            {
              title: 'Bulk Requests',
              path: localRoutes.bulkFaceVerifications,
              roles: [appPermissions.BULK_FACE_VERIFICATION_TRIGGER, appPermissions.BULK_FACE_VERIFICATION_VIEW]
            },
          ]
        },
        {
          title: productLabel(ProductCategory.FaceMatch),
          icon: <FaceOutlinedIcon fontSize="small"/>,
          path: localRoutes.faceMatch,
          info: <NewLabel />,
          roles: [appPermissions.FACE_MATCH_TRIGGER, appPermissions.FACE_MATCH_VIEW],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.faceMatch,
              roles: [appPermissions.FACE_MATCH_TRIGGER, appPermissions.FACE_MATCH_VIEW],
            }
          ]
        },
        {
          title: productLabel(ProductCategory.PhoneVerification),
          icon: <PhoneIcon fontSize="small"/>,
          path: localRoutes.phoneNumberVerifications,
          roles: [appPermissions.PHONE_NUMBER_VERIFICATION_VIEW, appPermissions.PHONE_NUMBER_VERIFICATION_TRIGGER, appPermissions.BULK_PHONE_NUMBER_VERIFICATION_VIEW, appPermissions.BULK_PHONE_NUMBER_VERIFICATION_TRIGGER],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.phoneNumberVerifications,
              roles: [appPermissions.PHONE_NUMBER_VERIFICATION_VIEW, appPermissions.PHONE_NUMBER_VERIFICATION_TRIGGER,]
            },
            {
              title: 'Bulk Request',
              path: localRoutes.bulkPhoneNumberVerifications,
              roles: [appPermissions.BULK_PHONE_NUMBER_VERIFICATION_VIEW, appPermissions.BULK_PHONE_NUMBER_VERIFICATION_TRIGGER]
            },
          ]
        },
        {
          title: productLabel(ProductCategory.AmlScreening),
          icon: <FactCheckIcon fontSize="small"/>,
          path: localRoutes.amlScreeningRequests,
          roles: [appPermissions.AML_SCREENING_VIEW, appPermissions.AML_SCREENING_TRIGGER, appPermissions.BULK_AML_SCREENING_VIEW, appPermissions.BULK_AML_SCREENING_TRIGGER],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.amlScreeningRequests,
              roles: [appPermissions.AML_SCREENING_VIEW, appPermissions.AML_SCREENING_TRIGGER]
            },
            {
              title: 'Bulk Requests',
              path: localRoutes.bulkAmlScreeningRequests,
              roles: [appPermissions.BULK_AML_SCREENING_VIEW, appPermissions.BULK_AML_SCREENING_TRIGGER]
            },
          ]
        },
        {
          title: productLabel(ProductCategory.BusinessSearch),
          icon: <DomainVerifiedIcon fontSize="small"/>,
          path: localRoutes.businessSearches,
          roles: [appPermissions.BUSINESS_SEARCH_VIEW, appPermissions.BUSINESS_SEARCH_TRIGGER, appPermissions.BULK_BUSINESS_SEARCH_VIEW, appPermissions.BULK_BUSINESS_SEARCH_TRIGGER],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.businessSearches,
              roles: [appPermissions.BUSINESS_SEARCH_VIEW, appPermissions.BUSINESS_SEARCH_TRIGGER]
            },
            {
              title: 'Bulk Requests',
              path: localRoutes.bulkBusinessSearches,
              roles: [appPermissions.BULK_BUSINESS_SEARCH_TRIGGER]
            },
          ]
        },
        enableUssdConsent && {
          title: productLabel(ProductCategory.DataConsent),
          icon: <ApprovalIcon fontSize="small"/>,
          path: localRoutes.consentRequests,
          roles: [appPermissions.CONSENT_VIEW, appPermissions.CONSENT_TRIGGER, appPermissions.BULK_CONSENT_VIEW, appPermissions.BULK_CONSENT_TRIGGER],
          children: [
            {
              title: 'Single Requests',
              path: localRoutes.consentRequests,
              roles: [appPermissions.CONSENT_VIEW, appPermissions.CONSENT_TRIGGER],
            },
            {
              title: 'Bulk Requests',
              path: localRoutes.bulkConsentRequests,
              roles: [appPermissions.BULK_CONSENT_VIEW, appPermissions.BULK_CONSENT_TRIGGER],
            },
            {
              title: 'Templates',
              path: localRoutes.consentTemplates,
              roles: [appPermissions.CONSENT_TEMPLATE_VIEW, appPermissions.CONSENT_TEMPLATE_EDIT]
            },
            {
              title: 'Data Custodians',
              path: localRoutes.dataCustodians,
              roles: [appPermissions.CONSENT_DATA_CUSTODIAN_EDIT, appPermissions.CONSENT_DATA_CUSTODIAN_VIEW],
            },
          ]
        },
      ]
    },
    {
      title: 'Management',
      items: [
        {
          title: 'Staff',
          icon: <PeopleIcon fontSize="small"/>,
          path: localRoutes.staff,
          roles: [appPermissions.PARTNER_MEMBERS_VIEW, appPermissions.PARTNER_MEMBERS_EDIT]
        },
        {
          title: 'Partners',
          icon: <GroupWorkIcon fontSize="small"/>,
          path: localRoutes.partners,
          roles: [appPermissions.PARTNERS_EDIT, appPermissions.PARTNERS_VIEW],
          requiresPrimaryUser: true
        },
        {
          title: 'Settings',
          icon: <SettingIcon fontSize="small"/>,
          path: localRoutes.settings,
          roles: [appPermissions.PARTNER_SETTINGS_VIEW, appPermissions.PARTNERS_EDIT]
        },
        {
          title: 'Invoices',
          icon: <ReceiptIcon fontSize="small"/>,
          path: localRoutes.invoices,
          roles: [appPermissions.VIEW_INVOICES, ...rootAdminUserRoles],
          requiresAllRoles: true,
          requiresPrimaryUser: true
        },
        {
          title: 'Price Bands',
          icon: <SellIcon fontSize="small"/>,
          path: localRoutes.priceBands,
          roles: [appPermissions.VIEW_PRICE_BANDS, ...rootAdminUserRoles],
          requiresAllRoles: true,
          requiresPrimaryUser: true
        }
      ]
    },
  ];
};

const DashboardSidebar: FC<React.PropsWithChildren<DashboardSidebarProps>> = (props) => {
  const { onMobileClose, openMobile } = props;
  const location = useLocation();
  const { user } = useAuth();
  const theme = useAppTheme();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const enableCustomerOnboarding = remoteConfigData.enableCustomerOnboarding();
  const enablePeopleBulkImports = remoteConfigData.enablePeopleBulkImports();
  const enableCompanyBulkImports = remoteConfigData.enableCompanyBulkImports();
  const enableUssdConsent = remoteConfigData.enableUssdConsent();

  const sections = getSections(
    enableCustomerOnboarding,
    enablePeopleBulkImports,
    enableCompanyBulkImports,
    enableUssdConsent
  );

  const sectionsData = useMemo<Section[]>(() => sections
    .map((it) => ({ ...it, items: getItems(it.items, user) }))
    .filter((it) => hasValue(it.items)), [user, sections]);

  const content = (
        <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
        >
            <Scrollbar options={{ suppressScrollX: true }}>
                <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}
                >
                    <Box
                        sx={{
                          display: {
                            lg: 'flex',
                            md: 'none',
                          },
                          justifyContent: 'left',
                          alignItems: 'center',
                          p: 2
                        }}
                    >
                        <RouterLink to="/">
                            <VerifyLogo
                                sx={{
                                  width: 50,
                                  height: 50,
                                }}
                            />
                        </RouterLink>
                        <Box
                            sx={{
                              ml: 1
                            }}
                        >
                            <Typography
                                color={theme.sideNav.text.primary}
                                variant="h6"
                            >
                                {appSettings.appName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Box
                            sx={{
                              alignItems: 'center',
                              backgroundColor: theme.sideNav.background.default,
                              borderRadius: 1,
                              display: 'flex',
                              overflow: 'auto',
                              p: 2
                            }}
                        >
                            <Box>
                                <Typography
                                    color={theme.sideNav.text.primary}
                                    variant="subtitle2"
                                >
                                    {toTitleCase(user.name)}
                                </Typography>
                                <Typography
                                    color={theme.sideNav.text.secondary}
                                    variant="body2"
                                >
                                    {user.email}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Divider/>
                    <Box sx={{ p: 2 }}>
                        {sectionsData.map((section) => (
                            <NavSection
                                key={section.title}
                                pathname={location.pathname}
                                sx={{
                                  '& + &': {
                                    mt: 3
                                  }
                                }}
                                {...section}
                            />
                        ))}
                    </Box>
                    <Box sx={{
                      marginTop: 'auto',
                      width: '100%'
                    }}
                    >
                        <Divider/>
                        <Box
                            sx={{
                              px: 2,
                              py: 3
                            }}
                        >
                            <Button
                                color="primary"
                                fullWidth
                                sx={{
                                  mt: 2,
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                }}
                                href={remoteConfigData.feedbackSurveyUrl()}
                                target="_blank"
                                variant="text"
                            >
                                Give us feedback
                            </Button>
                            <ContactUsButton title="Contact us"/>
                            <Button
                                color="primary"
                                fullWidth
                                sx={{ mt: 2 }}
                                href={remoteRoutes.apiDocumentation}
                                target="_blank"
                                variant="contained"
                            >
                                API Documentation
                            </Button>

                        </Box>
                    </Box>
                </Box>
            </Scrollbar>
        </Box>
  );

  return (
        <>
            <Drawer
                anchor="left"
                onClose={onMobileClose}
                open={openMobile}
                PaperProps={{
                  sx: {
                    backgroundColor: theme.sideNav.background.paper,
                    width: 290,
                    display: {
                      lg: 'none',
                      md: 'flex'
                    }
                  }
                }}
                variant="temporary"
            >
                {content}
            </Drawer>
            <Drawer
                anchor="left"
                open
                PaperProps={{
                  sx: {
                    backgroundColor: theme.sideNav.background.paper,
                    height: '100%',
                    width: 290,
                    border: 'none',
                    display: {
                      lg: 'flex',
                      md: 'none',
                      sm: 'none',
                      xs: 'none',
                    }
                  }
                }}
                variant="persistent"
            >
                {content}
            </Drawer>
        </>
  );
};

export default DashboardSidebar;
